import { Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";

// Loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center h-[60vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002FA7]"></div>
  </div>
);

// Eager-loaded (always needed for auth flow)
import Login from "../pages/Login";
import Register from "../pages/Register";
import ResetPassword from "../pages/ResetPassword";
import Landing from "../pages/Landing";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import TermsOfService from "../pages/TermsOfService";

// Lazy-loaded pages
const EarlyAdopter = lazy(() => import("../pages/EarlyAdopter"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const Clients = lazy(() => import("../pages/Clients"));
const ClientDetail = lazy(() => import("../pages/ClientDetail"));
const Projects = lazy(() => import("../pages/Projects"));
const TimeTracking = lazy(() => import("../pages/TimeTracking"));
const Users = lazy(() => import("../pages/Users"));
const AdminDashboard = lazy(() => import("../pages/AdminDashboard"));
const FirmDetail = lazy(() => import("../pages/FirmDetail"));
const Profile = lazy(() => import("../pages/Profile"));
const Analytics = lazy(() => import("../pages/Analytics"));
const Retainers = lazy(() => import("../pages/Retainers"));
const Invoices = lazy(() => import("../pages/Invoices"));
const Alerts = lazy(() => import("../pages/Alerts"));
const Capacity = lazy(() => import("../pages/Capacity"));
const Billing = lazy(() => import("../pages/Billing"));
const AdvisorDashboard = lazy(() => import("../pages/AdvisorDashboard"));
const AdvisorFirmKPIs = lazy(() => import("../pages/AdvisorFirmKPIs"));
const AdvisorProfitability = lazy(() => import("../pages/AdvisorProfitability"));
const CFOWeeklyPack = lazy(() => import("../pages/CFOWeeklyPack"));
const WeeklyClose = lazy(() => import("../pages/WeeklyClose"));
const SuperAdminDashboard = lazy(() => import("../pages/SuperAdminDashboard"));
const Reports = lazy(() => import("../pages/Reports"));
const Subscriptions = lazy(() => import("../pages/Subscriptions"));
const Approvals = lazy(() => import("../pages/Approvals"));
const EarlyAdopterAdmin = lazy(() => import("../pages/EarlyAdopterAdmin"));
const RateManagement = lazy(() => import("../pages/RateManagement"));
const AccountingDashboard = lazy(() => import("../pages/AccountingDashboard"));
const AccountingSettings = lazy(() => import("../pages/AccountingSettings"));
const DatabaseBackup = lazy(() => import("../pages/DatabaseBackup"));
const PeriodClose = lazy(() => import("../pages/PeriodClose"));
const DocumentosTributarios = lazy(() => import("../pages/DocumentosTributarios"));
const FirmSettings = lazy(() => import("../pages/FirmSettings"));
const AIAnalysis = lazy(() => import("../pages/AIAnalysis"));
const ProjectManagement = lazy(() => import("../pages/ProjectManagement"));
const ClientReports = lazy(() => import("../pages/ClientReports"));

// Portal Pages (lazy)
const PortalLogin = lazy(() => import("../pages/portal/PortalLogin"));
const PortalResetPassword = lazy(() => import("../pages/portal/PortalResetPassword"));
const PortalLayout = lazy(() => import("../pages/portal/PortalLayout"));
const PortalDashboard = lazy(() => import("../pages/portal/PortalDashboard"));
const PortalRetainers = lazy(() => import("../pages/portal/PortalRetainers"));
const PortalProjects = lazy(() => import("../pages/portal/PortalProjects"));
const PortalActivities = lazy(() => import("../pages/portal/PortalActivities"));

// Components
import Layout from "../components/Layout";
import { ProtectedRoute, DefaultRedirect } from "./ProtectedRoute";

const AppRouter = () => {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/early-adopter" element={<EarlyAdopter />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Protected routes with Layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:clientId" element={<ClientDetail />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/project-management" element={
            <ProjectManagement />
        } />
        <Route path="/time" element={<TimeTracking />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/analytics" element={
          <ProtectedRoute adminOnly>
            <Analytics />
          </ProtectedRoute>
        } />
        <Route path="/retainers" element={
          <ProtectedRoute adminOnly allowContador>
            <Retainers />
          </ProtectedRoute>
        } />
        <Route path="/invoices" element={
          <ProtectedRoute adminOnly>
            <Invoices />
          </ProtectedRoute>
        } />
        <Route
          path="/billing"
          element={
            <ProtectedRoute adminOnly>
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alerts"
          element={
            <ProtectedRoute adminOnly>
              <Alerts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/capacity"
          element={
            <ProtectedRoute adminOnly>
              <Capacity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute adminOnly>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rates"
          element={
            <ProtectedRoute adminOnly>
              <RateManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute adminOnly>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute superadminOnly>
              <SuperAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute superadminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/firms/:firmId"
          element={
            <ProtectedRoute superadminOnly>
              <FirmDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advisor"
          element={
            <ProtectedRoute superadminOnly>
              <AdvisorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advisor/profitability"
          element={
            <ProtectedRoute superadminOnly>
              <AdvisorProfitability />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advisor/firms/:firmId"
          element={
            <ProtectedRoute superadminOnly>
              <AdvisorFirmKPIs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advisor/firms/:firmId/cfo-pack"
          element={
            <ProtectedRoute superadminOnly>
              <CFOWeeklyPack />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions"
          element={
            <ProtectedRoute superadminOnly>
              <Subscriptions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/early-adopter-admin"
          element={
            <ProtectedRoute superadminOnly>
              <EarlyAdopterAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/database-backup"
          element={
            <ProtectedRoute superadminOnly>
              <DatabaseBackup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/weekly-close"
          element={
            <ProtectedRoute adminOnly>
              <WeeklyClose />
            </ProtectedRoute>
          }
        />
        <Route
          path="/approvals"
          element={
            <ProtectedRoute adminOnly>
              <Approvals />
            </ProtectedRoute>
          }
        />
        {/* Accounting routes */}
        <Route
          path="/accounting"
          element={
            <ProtectedRoute>
              <AccountingDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounting/settings"
          element={
            <ProtectedRoute adminOnly>
              <AccountingSettings />
            </ProtectedRoute>
          }
        />
        {/* Chile Billing Routes */}
        <Route
          path="/period-close"
          element={
            <ProtectedRoute adminOnly allowContador>
              <PeriodClose />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documentos-tributarios"
          element={
            <ProtectedRoute adminOnly allowContador>
              <DocumentosTributarios />
            </ProtectedRoute>
          }
        />
        {/* Firm Settings */}
        <Route
          path="/firm-settings"
          element={
            <ProtectedRoute adminOnly>
              <FirmSettings />
            </ProtectedRoute>
          }
        />
        {/* Client Reports */}
        <Route
          path="/client-reports"
          element={
            <ProtectedRoute adminOnly allowContador>
              <ClientReports />
            </ProtectedRoute>
          }
        />
        {/* AI Analysis - SuperAdmin Only */}
        <Route
          path="/ai-analysis"
          element={
            <ProtectedRoute superadminOnly>
              <AIAnalysis />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Client Portal Routes */}
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/activate" element={<PortalLogin />} />
      <Route path="/portal/reset-password" element={<PortalResetPassword />} />
      <Route path="/portal" element={<PortalLayout />}>
        <Route index element={<PortalDashboard />} />
        <Route path="retainers" element={<PortalRetainers />} />
        <Route path="projects" element={<PortalProjects />} />
        <Route path="projects/:projectId" element={<PortalActivities />} />
      </Route>
      
      {/* Catch all - smart redirect based on role */}
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
    </Suspense>
  );
};

export default AppRouter;
